import React, { useState, useEffect } from 'react';
import { Box, Button, Typography, Card, CardContent, Grid, Switch, FormControlLabel, Slider } from '@mui/material';
import { FuturisticLoader } from './FuturisticLoader';

export const LoaderDemo: React.FC = () => {
  const [isLoading, setIsLoading] = useState(false);
  const [progress, setProgress] = useState(0);
  const [enableSound, setEnableSound] = useState(true);
  const [size, setSize] = useState<'small' | 'medium' | 'large'>('medium');
  const [showProgress, setShowProgress] = useState(false);

  const handleStartLoading = () => {
    setIsLoading(true);
    setProgress(0);
    
    // Simulate progress
    const interval = setInterval(() => {
      setProgress(prev => {
        if (prev >= 100) {
          clearInterval(interval);
          setTimeout(() => setIsLoading(false), 1000);
          return 100;
        }
        return prev + Math.random() * 15;
      });
    }, 500);
  };

  const handleStopLoading = () => {
    setIsLoading(false);
    setProgress(0);
  };

  return (
    <Box sx={{ p: 4, minHeight: '100vh', background: 'linear-gradient(135deg, #0f172a 0%, #1e293b 100%)' }}>
      <Typography 
        variant="h3" 
        sx={{ 
          textAlign: 'center', 
          mb: 4,
          background: 'linear-gradient(135deg, #6366f1 0%, #06b6d4 100%)',
          WebkitBackgroundClip: 'text',
          WebkitTextFillColor: 'transparent',
          fontWeight: 800,
        }}
      >
        Futuristic Loader Demo
      </Typography>

      <Grid container spacing={4}>
        {/* Controls */}
        <Grid item xs={12} md={4}>
          <Card sx={{ 
            background: 'rgba(255, 255, 255, 0.05)', 
            backdropFilter: 'blur(10px)',
            border: '1px solid rgba(99, 102, 241, 0.2)',
          }}>
            <CardContent>
              <Typography variant="h6" sx={{ mb: 3, color: '#6366f1' }}>
                Controls
              </Typography>
              
              <Box sx={{ mb: 3 }}>
                <Button
                  variant="contained"
                  fullWidth
                  onClick={handleStartLoading}
                  disabled={isLoading}
                  sx={{
                    mb: 2,
                    background: 'linear-gradient(135deg, #6366f1 0%, #06b6d4 100%)',
                    '&:hover': {
                      background: 'linear-gradient(135deg, #4f46e5 0%, #0891b2 100%)',
                    },
                  }}
                >
                  Start Loading
                </Button>
                
                <Button
                  variant="outlined"
                  fullWidth
                  onClick={handleStopLoading}
                  disabled={!isLoading}
                  sx={{
                    borderColor: '#ef4444',
                    color: '#ef4444',
                    '&:hover': {
                      borderColor: '#dc2626',
                      backgroundColor: 'rgba(239, 68, 68, 0.1)',
                    },
                  }}
                >
                  Stop Loading
                </Button>
              </Box>

              <FormControlLabel
                control={
                  <Switch
                    checked={enableSound}
                    onChange={(e) => setEnableSound(e.target.checked)}
                    sx={{
                      '& .MuiSwitch-switchBase.Mui-checked': {
                        color: '#6366f1',
                      },
                      '& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track': {
                        backgroundColor: '#6366f1',
                      },
                    }}
                  />
                }
                label="Enable Sound Effects"
                sx={{ color: 'white', mb: 2 }}
              />

              <FormControlLabel
                control={
                  <Switch
                    checked={showProgress}
                    onChange={(e) => setShowProgress(e.target.checked)}
                    sx={{
                      '& .MuiSwitch-switchBase.Mui-checked': {
                        color: '#6366f1',
                      },
                      '& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track': {
                        backgroundColor: '#6366f1',
                      },
                    }}
                  />
                }
                label="Show Progress Bar"
                sx={{ color: 'white', mb: 3 }}
              />

              <Typography variant="body2" sx={{ color: 'white', mb: 1 }}>
                Size: {size}
              </Typography>
              <Box sx={{ display: 'flex', gap: 1, mb: 3 }}>
                {(['small', 'medium', 'large'] as const).map((sizeOption) => (
                  <Button
                    key={sizeOption}
                    variant={size === sizeOption ? 'contained' : 'outlined'}
                    size="small"
                    onClick={() => setSize(sizeOption)}
                    sx={{
                      minWidth: '60px',
                      ...(size === sizeOption ? {
                        background: 'linear-gradient(135deg, #6366f1 0%, #06b6d4 100%)',
                      } : {
                        borderColor: '#6366f1',
                        color: '#6366f1',
                      }),
                    }}
                  >
                    {sizeOption}
                  </Button>
                ))}
              </Box>

              {showProgress && (
                <Box>
                  <Typography variant="body2" sx={{ color: 'white', mb: 1 }}>
                    Progress: {Math.round(progress)}%
                  </Typography>
                  <Slider
                    value={progress}
                    onChange={(_, value) => setProgress(value as number)}
                    min={0}
                    max={100}
                    sx={{
                      '& .MuiSlider-track': {
                        background: 'linear-gradient(90deg, #6366f1, #06b6d4)',
                      },
                      '& .MuiSlider-thumb': {
                        backgroundColor: '#6366f1',
                      },
                    }}
                  />
                </Box>
              )}
            </CardContent>
          </Card>
        </Grid>

        {/* Loader Display */}
        <Grid item xs={12} md={8}>
          <Card sx={{ 
            background: 'rgba(255, 255, 255, 0.05)', 
            backdropFilter: 'blur(10px)',
            border: '1px solid rgba(99, 102, 241, 0.2)',
            minHeight: '400px',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
          }}>
            <CardContent sx={{ width: '100%', textAlign: 'center' }}>
              {isLoading ? (
                <FuturisticLoader
                  message="Processing Request"
                  showProgress={showProgress}
                  progress={progress}
                  size={size}
                  enableSound={enableSound}
                />
              ) : (
                <Box sx={{ py: 8 }}>
                  <Typography variant="h5" sx={{ color: '#6366f1', mb: 2 }}>
                    Ready to Load
                  </Typography>
                  <Typography variant="body1" sx={{ color: 'rgba(255, 255, 255, 0.7)' }}>
                    Click "Start Loading" to see the futuristic loader in action
                  </Typography>
                </Box>
              )}
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      {/* Features */}
      <Box sx={{ mt: 6 }}>
        <Typography variant="h5" sx={{ color: '#6366f1', mb: 3, textAlign: 'center' }}>
          Features
        </Typography>
        <Grid container spacing={3}>
          {[
            {
              title: 'Audio Feedback',
              description: 'Optional sound effects using Web Audio API for immersive experience',
              icon: '🔊',
            },
            {
              title: 'Dynamic Messages',
              description: 'Automatically cycles through different loading messages',
              icon: '💬',
            },
            {
              title: 'Progress Tracking',
              description: 'Optional progress bar with real-time updates',
              icon: '📊',
            },
            {
              title: 'Multiple Sizes',
              description: 'Small, medium, and large variants for different use cases',
              icon: '📏',
            },
            {
              title: 'Sci-Fi Design',
              description: 'Futuristic animations with orbital rings and glowing effects',
              icon: '🚀',
            },
            {
              title: 'Responsive',
              description: 'Works perfectly on all screen sizes and devices',
              icon: '📱',
            },
          ].map((feature, index) => (
            <Grid item xs={12} sm={6} md={4} key={index}>
              <Card sx={{ 
                background: 'rgba(255, 255, 255, 0.05)', 
                backdropFilter: 'blur(10px)',
                border: '1px solid rgba(99, 102, 241, 0.2)',
                height: '100%',
              }}>
                <CardContent sx={{ textAlign: 'center' }}>
                  <Typography variant="h2" sx={{ mb: 2 }}>
                    {feature.icon}
                  </Typography>
                  <Typography variant="h6" sx={{ color: '#6366f1', mb: 1 }}>
                    {feature.title}
                  </Typography>
                  <Typography variant="body2" sx={{ color: 'rgba(255, 255, 255, 0.7)' }}>
                    {feature.description}
                  </Typography>
                </CardContent>
              </Card>
            </Grid>
          ))}
        </Grid>
      </Box>
    </Box>
  );
};

export default LoaderDemo; 